import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import SectionCard from '../../components/admin/SectionCard';
import { EmptyState, LoadingState } from '../../components/admin/StateViews';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import { ChallengeService } from '../../services/ChallengeService';
import { AdminChallenge, AdminChallengeFilters, ChallengeStatus } from '../../types';

const PAGE_SIZE = 10;

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

const formatAmount = (value: string | number) =>
  Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 });

const STATUS_COLORS: Record<ChallengeStatus, 'success' | 'warning' | 'error' | 'default'> = {
  ACTIVE: 'success',
  COMPLETED: 'default',
  EXPIRED: 'warning',
  CANCELLED: 'error',
};

const ChallengesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const canDelete = user?.role === 'SuperAdmin';

  const [challenges, setChallenges] = useState<AdminChallenge[]>([]);
  const [filters, setFilters] = useState<AdminChallengeFilters>({ status: '', includeDeleted: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pendingDelete, setPendingDelete] = useState<AdminChallenge | null>(null);

  const loadChallenges = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ChallengeService.getChallenges({
        status: filters.status || undefined,
        includeDeleted: filters.includeDeleted,
      });
      setChallenges(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load challenges.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadChallenges();
  }, [loadChallenges]);

  const handleDelete = async (challenge: AdminChallenge) => {
    try {
      await ChallengeService.deleteChallenge(challenge.id);
      showToast('Challenge deleted.', 'success');
      loadChallenges();
    } catch {
      showToast('Failed to delete challenge.', 'error');
    } finally {
      setPendingDelete(null);
    }
  };

  const paginated = challenges.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageCount = Math.ceil(challenges.length / PAGE_SIZE);

  return (
    <AdminPage
      title="Challenges"
      description="View and manage all fundraising challenges."
      action={
        <Button
          variant="contained"
          startIcon={<AddCircleOutline />}
          onClick={() => navigate('/challenges/new')}
        >
          New Challenge
        </Button>
      }
    >
      <SectionCard title="Filters">
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Select
            size="small"
            value={filters.status ?? ''}
            displayEmpty
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="EXPIRED">Expired</MenuItem>
            <MenuItem value="CANCELLED">Cancelled</MenuItem>
          </Select>
          <TextField
            size="small"
            label="Cause ID"
            value={filters.causeId ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, causeId: e.target.value || undefined }))}
          />
        </Stack>
      </SectionCard>

      <SectionCard title={`Challenges (${challenges.length})`}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <LoadingState />
        ) : challenges.length === 0 ? (
          <EmptyState title="No challenges found." description="Create a new challenge to get started." />
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Target</TableCell>
                  <TableCell align="right">Raised</TableCell>
                  <TableCell align="right">%</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Creator</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((ch) => (
                  <TableRow
                    key={ch.id}
                    hover
                    sx={{ cursor: 'pointer', opacity: ch.deletedAt ? 0.5 : 1 }}
                  >
                    <TableCell
                      onClick={() => navigate(`/challenges/${ch.id}`)}
                      sx={{ fontWeight: 600, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {ch.title}
                    </TableCell>
                    <TableCell onClick={() => navigate(`/challenges/${ch.id}`)}>
                      <Chip
                        label={ch.status}
                        size="small"
                        color={STATUS_COLORS[ch.status] ?? 'default'}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={() => navigate(`/challenges/${ch.id}`)}>
                      {ch.currency} {formatAmount(ch.targetAmount)}
                    </TableCell>
                    <TableCell align="right" onClick={() => navigate(`/challenges/${ch.id}`)}>
                      {ch.currency} {formatAmount(ch.actualAmount)}
                    </TableCell>
                    <TableCell align="right" onClick={() => navigate(`/challenges/${ch.id}`)}>
                      {Math.min(Math.round(ch.percentage), 100)}%
                    </TableCell>
                    <TableCell onClick={() => navigate(`/challenges/${ch.id}`)}>
                      {formatDate(ch.endDate)}
                    </TableCell>
                    <TableCell onClick={() => navigate(`/challenges/${ch.id}`)}>
                      {ch.creator.displayName ?? ch.createdById.slice(0, 8)}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => navigate(`/challenges/${ch.id}/edit`)}>
                        Edit
                      </Button>
                      {canDelete && !ch.deletedAt && (
                        <Button size="small" color="error" onClick={() => setPendingDelete(ch)}>
                          Delete
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {pageCount > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} />
              </Box>
            )}
          </>
        )}
      </SectionCard>

      {pendingDelete && (
        <ConfirmDialog
          open
          title="Delete Challenge"
          description={`Are you sure you want to delete "${pendingDelete.title}"? This cannot be undone.`}
          onConfirm={() => handleDelete(pendingDelete)}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </AdminPage>
  );
};

export default ChallengesListPage;
